// SSE-Konsumer fuer POST /chats/{id}/messages (Atlas-Task a823c24a).
//
// EventSource (browser-native) kann kein POST + custom-Header und ist
// damit fuer unsere Bearer-Auth + Body-Request unbrauchbar. Wir machen
// fetch() + ReadableStream-Reader manuell — Standard-Pattern fuer
// SSE-mit-POST.
import { apiUrl, authToken, ApiError } from "./api";

export interface StreamEvents {
  onUserMsg?: (msg: ChatStreamMessage) => void;
  onAssistantMsg?: (msg: ChatStreamMessage) => void;
  onSources?: (sources: unknown[]) => void;
  onDelta?: (text: string) => void;
  onTrace?: (event: TraceEvent) => void;
  onMeta?: (meta: ChatStreamMeta) => void;
  onDone?: (info: { assistant_msg_id: string }) => void;
  onError?: (err: Error) => void;
}

export interface ChatStreamMessage {
  id: string;
  chat_id: string;
  role: "user" | "assistant" | "system";
  content?: string;
  created_at?: string | null;
  // D6 Agent-Mode: bei agent_msg gesetzt
  mode?: "agent" | null;
  trigger?: string | null;
}

// Trace-Event vom Agent-Loop. Schema-Vereinbarung mit
// app/chats/agent_loop.py:_emit_trace.
export interface TraceEvent {
  type: string;
  iteration?: number;
  // trigger / initial_retrieve
  matched?: string;
  iteration_cap?: number;
  top_k?: number;
  hits?: number;
  kinds?: Record<string, number>;
  // thinking / reasoning
  model?: string;
  text?: string;
  // tool_use / tool_result
  tool?: string;
  input?: Record<string, unknown>;
  output_summary?: string;
  presentation_id?: string | null;
  deep_link?: string | null;
  // final / cancelled / llm_error / iteration_cap_reached / stream_error
  tokens_in?: number;
  tokens_out?: number;
  stop_reason?: string;
  reason?: string;
  error?: string;
  tokens_in_total?: number;
  tokens_out_total?: number;
  // Agent v2 (Atlas-Task ab56f6b4): Digest + Plan-Step.
  topic?: string;
  user_intent?: string;
  expected_deliverable?: string;
  key_facts?: string[];
  open_questions?: string[];
  deliverable?: string;
  rationale?: string;
  steps?: Array<{
    step?: string;
    tool?: string | null;
    acceptance_criteria?: string;
  }>;
  spannungsbogen?: string | null;
  _fallback?: boolean;
  // Agent v2 storyline-blueprint event
  title?: string;
  subtitle?: string;
  hook?: string;
  closing_message?: string;
  slide_arc?: Array<{
    position?: number;
    theme?: string;
    key_message?: string;
    hero_element?: string;
    supporting_facts?: string[];
  }>;
}

export interface ChatStreamMeta {
  // Chat-Stream-Pfad: nur Token-Stats. Agent-Pfad: zusaetzlich content,
  // sources_json (kommen am Ende statt als sources/delta-Events).
  model: string;
  tokens_in: number;
  tokens_out: number;
  cancelled?: boolean;
  error?: string | null;
  content?: string;
  sources_json?: unknown[] | null;
}

export interface ChatStreamBody {
  content: string;
  model?: string | null;
  top_k?: number;
  thinking_budget?: number | null;
  /** Recherche-Tiefe (off/normal/deep) für Web-Recherche im Agent-Modus. */
  research_mode?: "off" | "normal" | "deep";
}

/**
 * Sendet eine Chat-Message als SSE-Stream und ruft die passenden
 * Callback-Hooks pro Event-Type. Returnt eine Funktion zum Abbrechen
 * (AbortController.abort).
 */
export function postChatMessageStream(
  chatId: string,
  body: ChatStreamBody,
  events: StreamEvents,
): { cancel: () => void; done: Promise<void> } {
  const controller = new AbortController();
  const done = (async () => {
    try {
      const t = authToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (t) headers["Authorization"] = `Bearer ${t}`;

      const res = await fetch(apiUrl(`/chats/${chatId}/messages`), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        let detail = `HTTP ${res.status}`;
        try {
          const text = await res.text();
          detail = text || detail;
        } catch {
          /* swallow */
        }
        throw new ApiError(res.status, detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });

        // SSE-Frames sind durch \n\n getrennt
        let frameEnd: number;
        while ((frameEnd = buf.indexOf("\n\n")) !== -1) {
          const rawFrame = buf.slice(0, frameEnd);
          buf = buf.slice(frameEnd + 2);
          const parsed = parseFrame(rawFrame);
          if (parsed) {
            dispatch(parsed.event, parsed.data, events);
          }
        }
      }
      // Nach Stream-Ende ggf. letzter Puffer (sollte leer sein, aber sicher)
      if (buf.trim()) {
        const parsed = parseFrame(buf);
        if (parsed) dispatch(parsed.event, parsed.data, events);
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        return; // User-Cancel — kein onError
      }
      events.onError?.(err as Error);
    }
  })();

  return { cancel: () => controller.abort(), done };
}

function parseFrame(raw: string): { event: string; data: unknown } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(dataStr) };
  } catch {
    return { event, data: dataStr };
  }
}

function dispatch(
  event: string,
  data: unknown,
  events: StreamEvents,
): void {
  switch (event) {
    case "user_msg":
      events.onUserMsg?.(data as ChatStreamMessage);
      return;
    case "assistant_msg":
      events.onAssistantMsg?.(data as ChatStreamMessage);
      return;
    case "sources":
      events.onSources?.(Array.isArray(data) ? data : []);
      return;
    case "delta":
      events.onDelta?.((data as { text: string }).text ?? "");
      return;
    case "trace":
      events.onTrace?.(data as TraceEvent);
      return;
    case "meta":
      events.onMeta?.(data as ChatStreamMeta);
      return;
    case "done":
      events.onDone?.(data as { assistant_msg_id: string });
      return;
    default:
      /* unknown event — ignore */
      return;
  }
}
